import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useSpring, animated, config } from 'react-spring';
import '../styles/Projects.css';
import { projectCollection } from '../data/projectData';
import { LOCALES, localizeText } from '../utils/localize';

const FEATURED_PROJECT_COUNT = 3;
const PHONE_MODEL_SRC = `${process.env.PUBLIC_URL}/3D_smartphone.glb`;
const SHOWCASE_ORBIT_BASE = 25;
const SHOWCASE_ROTATION_DURATION = 1480;
const SHOWCASE_AUTO_ROTATE_INTERVAL = 3600;
const SHOWCASE_PROJECT_SWAP_ROTATION_PROGRESS = 0.5;
const SHOWCASE_PROJECT_SWAP_TIME_PROGRESS = 1 - Math.pow(1 - SHOWCASE_PROJECT_SWAP_ROTATION_PROGRESS, 1 / 3);
const SCREEN_MATERIAL_NAME = 'BsXHDwLKqtDOfrW';
const SCREEN_TEXTURE_WIDTH = 1024;
const SCREEN_TEXTURE_HEIGHT = 2048;
const SHOWCASE_CAMERA_PHI = 88;
const SHOWCASE_CAMERA_RADIUS = 115;
const PROJECT_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'embedded', label: 'Embedded' },
  { id: 'systems', label: 'Systems' },
  { id: 'robotics', label: 'Robotics' },
  { id: 'iot', label: 'IoT' },
];

const formatShowcaseCameraOrbit = (theta) => `${theta}deg ${SHOWCASE_CAMERA_PHI}deg ${SHOWCASE_CAMERA_RADIUS}%`;

const loadAssetImage = (src) => new Promise((resolve, reject) => {
  const image = new Image();
  image.decoding = 'async';
  image.crossOrigin = 'anonymous';
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error(`Failed to load image: ${src}`));
  image.src = src;
});

const drawContainedImage = (context, image, bounds) => {
  const scale = Math.min(bounds.width / image.width, bounds.height / image.height);
  const width = image.width * scale;
  const height = image.height * scale;
  const x = bounds.x + (bounds.width - width) / 2;
  const y = bounds.y + (bounds.height - height) / 2;

  context.drawImage(image, x, y, width, height);
};

const drawCoveredImage = (context, image, bounds) => {
  const scale = Math.max(bounds.width / image.width, bounds.height / image.height);
  const width = image.width * scale;
  const height = image.height * scale;
  const x = bounds.x + (bounds.width - width) / 2;
  const y = bounds.y + (bounds.height - height) / 2;

  context.drawImage(image, x, y, width, height);
};

const toHashtag = (value) => `#${String(value ?? '').replace(/\s+/g, '')}`;

const localizeArrayValues = (values, L) => (values ?? []).map((value) => L(value));

const renderWindowStatusCode = (value) => {
  if (!value) {
    return null;
  }

  const statusMatch = value.match(/^([A-Za-z_$][\w$]*)(\.)([A-Za-z_$][\w$]*)(\(\);)$/);

  if (!statusMatch) {
    return <span className="project-window-code-plain">{value}</span>;
  }

  const [, scope, dot, method, tail] = statusMatch;

  return (
    <>
      <span className="project-window-code-scope">{scope}</span>
      <span className="project-window-code-dot">{dot}</span>
      <span className="project-window-code-method">{method}</span>
      <span className="project-window-code-tail">{tail}</span>
    </>
  );
};

const getWindowStatusTone = (effectName) => {
  if (effectName === 'close-glow') {
    return 'error';
  }

  if (effectName === 'minimize-preview') {
    return 'warning';
  }

  if (effectName === 'fullscreen-glow' || effectName === 'restore-glow') {
    return 'success';
  }

  return 'neutral';
};

const getWindowStatusClassName = (notice, effectName) => [
  'project-window-status',
  notice ? 'is-visible' : '',
  `is-${getWindowStatusTone(effectName)}`,
].filter(Boolean).join(' ');

const localizeProject = (project, L) => ({
  ...project,
  category: L(project.category),
  role: L(project.role),
  scope: L(project.scope),
  headline: L(project.headline),
  summary: L(project.summary),
  prototypeTabTitle: project.prototypeTabTitle ? L(project.prototypeTabTitle) : project.prototypeTabTitle,
  showcaseMedia: (project.showcaseMedia ?? []).map((media) => ({
    ...media,
    label: media.label ? L(media.label) : media.label,
  })),
  prototypeImages: (project.prototypeImages ?? []).map((image) => ({
    ...image,
    label: image.label ? L(image.label) : image.label,
  })),
  introVideo: project.introVideo ? {
    ...project.introVideo,
    label: project.introVideo.label ? L(project.introVideo.label) : project.introVideo.label,
  } : project.introVideo,
  introDocument: project.introDocument ? {
    ...project.introDocument,
    label: project.introDocument.label ? L(project.introDocument.label) : project.introDocument.label,
  } : project.introDocument,
  techStack: (project.techStack ?? []).map((group) => ({
    ...group,
    label: L(group.label),
    items: localizeArrayValues(group.items, L),
  })),
  contributions: localizeArrayValues(project.contributions, L),
  systemDiagram: project.systemDiagram
    ? {
      ...project.systemDiagram,
      imageAlt: project.systemDiagram.imageAlt ? L(project.systemDiagram.imageAlt) : project.systemDiagram.imageAlt,
      bridges: localizeArrayValues(project.systemDiagram.bridges, L),
      columns: (project.systemDiagram.columns ?? []).map((column) => ({
        ...column,
        title: L(column.title),
        blocks: (column.blocks ?? []).map((block) => ({
          ...block,
          title: L(block.title),
          items: localizeArrayValues(block.items, L),
        })),
      })),
    }
    : project.systemDiagram,
});

const buildShowcaseScreenTexture = async (media, accent) => {
  const canvas = document.createElement('canvas');
  canvas.width = SCREEN_TEXTURE_WIDTH;
  canvas.height = SCREEN_TEXTURE_HEIGHT;

  const context = canvas.getContext('2d');

  if (!context) {
    return null;
  }

  context.fillStyle = '#050506';
  context.fillRect(0, 0, canvas.width, canvas.height);

  const vignette = context.createRadialGradient(
    canvas.width * 0.5,
    canvas.height * 0.44,
    canvas.width * 0.08,
    canvas.width * 0.5,
    canvas.height * 0.5,
    canvas.width * 0.72,
  );
  vignette.addColorStop(0, 'rgba(255, 255, 255, 0.05)');
  vignette.addColorStop(1, 'rgba(255, 255, 255, 0)');
  context.fillStyle = vignette;
  context.fillRect(0, 0, canvas.width, canvas.height);

  const accentGlow = context.createRadialGradient(
    canvas.width * 0.5,
    canvas.height * 0.76,
    canvas.width * 0.04,
    canvas.width * 0.5,
    canvas.height * 0.76,
    canvas.width * 0.44,
  );
  accentGlow.addColorStop(0, `${accent}44`);
  accentGlow.addColorStop(1, 'rgba(0, 0, 0, 0)');
  context.fillStyle = accentGlow;
  context.fillRect(0, 0, canvas.width, canvas.height);

  if (media?.src) {
    const image = await loadAssetImage(media.src);

    context.save();
    context.shadowColor = 'rgba(0, 0, 0, 0.42)';
    context.shadowBlur = media.type === 'logo' ? 54 : 28;
    context.shadowOffsetY = media.type === 'logo' ? 30 : 18;

    if (media.type === 'logo') {
      drawContainedImage(context, image, {
        x: canvas.width * 0.16,
        y: canvas.height * 0.26,
        width: canvas.width * 0.68,
        height: canvas.height * 0.28,
      });
    } else {
      drawCoveredImage(context, image, {
        x: canvas.width * 0.04,
        y: canvas.height * 0.06,
        width: canvas.width * 0.92,
        height: canvas.height * 0.88,
      });
    }

    context.restore();
  }

  return canvas.toDataURL('image/png');
};

const Projects = ({ locale = LOCALES.KR }) => {
  const L = useCallback((text) => localizeText(text, locale), [locale]);
  const projectsRef = useRef(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [isArchiveOpen, setIsArchiveOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [detailWindowMode, setDetailWindowMode] = useState('default');
  const [detailWindowFx, setDetailWindowFx] = useState('');
  const [detailControlNotice, setDetailControlNotice] = useState('');
  const [archiveWindowMode, setArchiveWindowMode] = useState('default');
  const [archiveWindowFx, setArchiveWindowFx] = useState('');
  const [archiveControlNotice, setArchiveControlNotice] = useState('');
  const [isShowcaseRotating, setIsShowcaseRotating] = useState(false);
  const [isShowcaseVisible, setIsShowcaseVisible] = useState(true);
  const [showcaseCameraOrbit, setShowcaseCameraOrbit] = useState(formatShowcaseCameraOrbit(SHOWCASE_ORBIT_BASE));
  const localizedProjects = useMemo(
    () => projectCollection.map((project) => localizeProject(project, L)),
    [L],
  );
  const featuredProjects = localizedProjects.slice(0, FEATURED_PROJECT_COUNT);
  const visibleProjects = featuredProjects.filter((project) => activeFilter === 'all' || project.filters?.includes(activeFilter));
  const selectedProject = localizedProjects.find((project) => project.id === selectedProjectId) ?? null;
  const detailPeriod = selectedProject?.period ?? '';
  const detailHashtags = selectedProject
    ? [
      selectedProject.year,
      selectedProject.scope,
      selectedProject.role,
      selectedProject.duration,
    ].filter(Boolean).map(toHashtag)
    : [];
  const showcaseFrames = featuredProjects.map((project) => ({
    id: project.id,
    project,
    media: project.showcaseMedia?.[0] ?? { type: 'empty' },
  }));
  const [showcaseIndex, setShowcaseIndex] = useState(0);
  const activeShowcaseFrame = showcaseFrames[showcaseIndex] ?? null;
  const showcaseProject = activeShowcaseFrame?.project ?? featuredProjects[0];
  const showcaseIndexRef = useRef(showcaseIndex);
  const showcaseTimersRef = useRef([]);
  const showcaseFrameRequestRef = useRef(null);
  const showcaseOrbitRef = useRef(SHOWCASE_ORBIT_BASE);
  const isShowcaseVisibleRef = useRef(true);
  const detailFxTimeoutRef = useRef(null);
  const detailCloseTimeoutRef = useRef(null);
  const archiveFxTimeoutRef = useRef(null);
  const archiveCloseTimeoutRef = useRef(null);

  const stopShowcaseMotion = useCallback(() => {
    showcaseTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    showcaseTimersRef.current = [];

    if (showcaseFrameRequestRef.current) {
      window.cancelAnimationFrame(showcaseFrameRequestRef.current);
      showcaseFrameRequestRef.current = null;
    }

    setIsShowcaseRotating(false);
  }, []);

  const resetShowcaseCamera = useCallback(() => {
    stopShowcaseMotion();
    showcaseOrbitRef.current = SHOWCASE_ORBIT_BASE;
    setShowcaseCameraOrbit(formatShowcaseCameraOrbit(SHOWCASE_ORBIT_BASE));
  }, [stopShowcaseMotion]);

  useEffect(() => {
    showcaseIndexRef.current = showcaseIndex;
  }, [showcaseIndex]);

  useEffect(() => {
    return () => {
      stopShowcaseMotion();
    };
  }, [stopShowcaseMotion]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'test' || typeof window === 'undefined') {
      return undefined;
    }

    if (window.customElements?.get('model-viewer')) {
      return undefined;
    }

    import('@google/model-viewer');

    return undefined;
  }, []);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;

    if (isArchiveOpen || selectedProjectId) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isArchiveOpen, selectedProjectId]);

  useEffect(() => {
    if (selectedProjectId || isArchiveOpen) {
      resetShowcaseCamera();
    }
  }, [isArchiveOpen, resetShowcaseCamera, selectedProjectId]);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const projectsElement = projectsRef.current;

    if (!projectsElement) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        const nextIsVisible = entry.isIntersecting && entry.intersectionRatio > 0.18;

        if (nextIsVisible === isShowcaseVisibleRef.current) {
          return;
        }

        isShowcaseVisibleRef.current = nextIsVisible;
        setIsShowcaseVisible(nextIsVisible);
        resetShowcaseCamera();
      },
      { threshold: [0, 0.18, 0.4] },
    );

    observer.observe(projectsElement);

    return () => {
      observer.disconnect();
    };
  }, [resetShowcaseCamera]);

  const rotateShowcaseProject = useCallback((nextIndex) => {
    if (nextIndex < 0 || nextIndex >= showcaseFrames.length || nextIndex === showcaseIndexRef.current) {
      return;
    }

    stopShowcaseMotion();
    setIsShowcaseRotating(true);

    const duration = SHOWCASE_ROTATION_DURATION;
    const startAngle = showcaseOrbitRef.current;
    const targetAngle = startAngle + 360;
    let didSwapProject = false;
    const startTime = performance.now();
    const swapTimer = window.setTimeout(() => {
      didSwapProject = true;
      setShowcaseIndex(nextIndex);
    }, duration * SHOWCASE_PROJECT_SWAP_TIME_PROGRESS);

    showcaseTimersRef.current.push(swapTimer);

    const animateRotation = (timestamp) => {
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      const nextAngle = startAngle + (targetAngle - startAngle) * easedProgress;

      showcaseOrbitRef.current = nextAngle;
      setShowcaseCameraOrbit(formatShowcaseCameraOrbit(nextAngle));

      if (progress < 1) {
        showcaseFrameRequestRef.current = window.requestAnimationFrame(animateRotation);
        return;
      }

      if (!didSwapProject) {
        setShowcaseIndex(nextIndex);
      }

      showcaseOrbitRef.current = targetAngle;
      setShowcaseCameraOrbit(formatShowcaseCameraOrbit(targetAngle));
      setIsShowcaseRotating(false);
    };

    showcaseFrameRequestRef.current = window.requestAnimationFrame(animateRotation);
  }, [showcaseFrames.length, stopShowcaseMotion]);

  useEffect(() => {
    if (showcaseFrames.length <= 1 || selectedProjectId || isArchiveOpen || !isShowcaseVisible) {
      return undefined;
    }

    const rotationTimer = window.setInterval(() => {
      rotateShowcaseProject((showcaseIndexRef.current + 1) % showcaseFrames.length);
    }, SHOWCASE_AUTO_ROTATE_INTERVAL);

    return () => {
      window.clearInterval(rotationTimer);
    };
  }, [isArchiveOpen, isShowcaseVisible, rotateShowcaseProject, selectedProjectId, showcaseFrames.length]);

  useEffect(() => {
    const handlePortfolioReload = () => {
      setSelectedProjectId(null);
      setIsArchiveOpen(false);
      setActiveFilter('all');
      resetShowcaseCamera();
      setShowcaseIndex(0);
    };

    window.addEventListener('portfolio:reload', handlePortfolioReload);

    return () => {
      window.removeEventListener('portfolio:reload', handlePortfolioReload);
    };
  }, [resetShowcaseCamera]);

  useEffect(() => {
    window.clearTimeout(detailFxTimeoutRef.current);
    window.clearTimeout(detailCloseTimeoutRef.current);
    setDetailWindowMode('default');
    setDetailWindowFx('');
    setDetailControlNotice('');
  }, [selectedProjectId]);

  useEffect(() => {
    window.clearTimeout(archiveFxTimeoutRef.current);
    window.clearTimeout(archiveCloseTimeoutRef.current);
    setArchiveWindowMode('default');
    setArchiveWindowFx('');
    setArchiveControlNotice('');
  }, [isArchiveOpen]);

  useEffect(() => () => {
    window.clearTimeout(detailFxTimeoutRef.current);
    window.clearTimeout(detailCloseTimeoutRef.current);
    window.clearTimeout(archiveFxTimeoutRef.current);
    window.clearTimeout(archiveCloseTimeoutRef.current);
  }, []);

  const containerSpring = useSpring({
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    config: config.gentle,
  });

  const detailSpring = useSpring({
    from: { opacity: 0, transform: 'translateY(10px)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    config: config.default,
  });

  const handleOpenProject = (projectId) => {
    const featuredIndex = featuredProjects.findIndex((project) => project.id === projectId);
    const isClosingCurrentProject = selectedProjectId === projectId;

    if (!isClosingCurrentProject && featuredIndex >= 0) {
      resetShowcaseCamera();
      setShowcaseIndex(featuredIndex);
    }

    setSelectedProjectId((currentProjectId) => (currentProjectId === projectId ? null : projectId));
  };

  const openProjectFromArchive = (projectId) => {
    const isFeaturedProject = featuredProjects.some((project) => project.id === projectId);

    setIsArchiveOpen(false);

    const featuredIndex = featuredProjects.findIndex((project) => project.id === projectId);

    if (featuredIndex >= 0) {
      resetShowcaseCamera();
      setShowcaseIndex(featuredIndex);
    }

    if (!isFeaturedProject) {
      resetShowcaseCamera();
    }

    setSelectedProjectId(projectId);
  };

  const pulseDetailWindow = (effectName, notice, duration = 1100) => {
    window.clearTimeout(detailFxTimeoutRef.current);
    setDetailWindowFx(effectName);
    setDetailControlNotice(notice);
    detailFxTimeoutRef.current = window.setTimeout(() => {
      setDetailWindowFx('');
      setDetailControlNotice('');
    }, duration);
  };

  const handleProjectDetailClose = () => {
    window.clearTimeout(detailCloseTimeoutRef.current);
    pulseDetailWindow('close-glow', 'window.close();', 420);
    detailCloseTimeoutRef.current = window.setTimeout(() => {
      setSelectedProjectId(null);
    }, 180);
  };

  const handleDetailMinimize = () => {
    pulseDetailWindow('minimize-preview', 'project.snapshot();', 1180);
  };

  const handleDetailFullscreenToggle = () => {
    const isFullscreen = detailWindowMode === 'fullscreen';

    setDetailWindowMode(isFullscreen ? 'default' : 'fullscreen');
    pulseDetailWindow(
      isFullscreen ? 'restore-glow' : 'fullscreen-glow',
      isFullscreen ? 'workspace.restore();' : 'workspace.maximize();',
      980,
    );
  };

  const pulseArchiveWindow = (effectName, notice, duration = 1100) => {
    window.clearTimeout(archiveFxTimeoutRef.current);
    setArchiveWindowFx(effectName);
    setArchiveControlNotice(notice);
    archiveFxTimeoutRef.current = window.setTimeout(() => {
      setArchiveWindowFx('');
      setArchiveControlNotice('');
    }, duration);
  };

  const handleArchiveClose = () => {
    window.clearTimeout(archiveCloseTimeoutRef.current);
    pulseArchiveWindow('close-glow', 'archive.close();', 420);
    archiveCloseTimeoutRef.current = window.setTimeout(() => {
      setIsArchiveOpen(false);
    }, 180);
  };

  const handleArchiveMinimize = () => {
    pulseArchiveWindow('minimize-preview', 'archive.compact();', 1180);
  };

  const handleArchiveFullscreenToggle = () => {
    const isFullscreen = archiveWindowMode === 'fullscreen';

    setArchiveWindowMode(isFullscreen ? 'default' : 'fullscreen');
    pulseArchiveWindow(
      isFullscreen ? 'restore-glow' : 'fullscreen-glow',
      isFullscreen ? 'archive.restore();' : 'archive.expand();',
      980,
    );
  };

  const syncShowcaseProject = (projectId) => {
    if (selectedProjectId || isArchiveOpen || !isShowcaseVisible) {
      return;
    }

    const featuredIndex = featuredProjects.findIndex((project) => project.id === projectId);

    if (featuredIndex >= 0) {
      rotateShowcaseProject(featuredIndex);
    }
  };

  const detailOverlayClassName = [
    'project-modal-overlay',
    detailWindowMode === 'fullscreen' ? 'is-fullscreen' : '',
  ].filter(Boolean).join(' ');

  const detailPanelClassName = [
    'project-modal-panel',
    detailWindowMode === 'fullscreen' ? 'is-fullscreen' : '',
    detailWindowFx ? `is-${detailWindowFx}` : '',
  ].filter(Boolean).join(' ');

  const archiveOverlayClassName = [
    'project-archive-overlay',
    archiveWindowMode === 'fullscreen' ? 'is-fullscreen' : '',
  ].filter(Boolean).join(' ');

  const archivePanelClassName = [
    'project-archive-panel',
    archiveWindowMode === 'fullscreen' ? 'is-fullscreen' : '',
    archiveWindowFx ? `is-${archiveWindowFx}` : '',
  ].filter(Boolean).join(' ');

  return (
    <section className="projects" id="projects" ref={projectsRef}>
      <animated.div style={containerSpring} className="projects-container section-shell">
        <div className="projects-showcase">
          <div className="projects-showcase-copy">
            <p className="section-eyebrow">Selected Work</p>
            <h2 className="section-title">{L('프로젝트')}</h2>
            <p className="projects-showcase-text">Embedded systems, device control, and software built for real-world interaction.</p>
            <div className="projects-showcase-current">
              <span>{showcaseProject?.category}</span>
              <strong>{showcaseProject?.title}</strong>
              <p>{showcaseProject?.headline}</p>
            </div>
          </div>
          <div className="projects-phone-stage" aria-hidden="true">
            <pre className="projects-code-bg">{`while (device.online) {
  readSensors();
  routeEvent();
  syncState();
}`}</pre>
            <div className="projects-device-aura" />
            <div className={`projects-device-shell ${isShowcaseRotating ? 'is-rotating' : ''}`}>
              <ProjectShowcaseDevice
                project={activeShowcaseFrame?.project ?? showcaseProject}
                media={activeShowcaseFrame?.media}
                cameraOrbit={showcaseCameraOrbit}
              />
            </div>
            <div className="projects-phone-dots">
              {showcaseFrames.map((frame, frameIndex) => (
                <span
                  key={`phone-dot-${frame.id}`}
                  className={`projects-phone-dot ${showcaseIndex === frameIndex ? 'active' : ''}`}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="projects-toolbar">
          <div className="projects-filters" role="tablist" aria-label="project filters">
            {PROJECT_FILTERS.map((filter) => (
              <button
                key={filter.id}
                type="button"
                className={`projects-filter ${activeFilter === filter.id ? 'active' : ''}`}
                onClick={() => setActiveFilter(filter.id)}
              >
                {filter.label}
              </button>
            ))}
          </div>
          <div className="projects-header-side">
            <span className="project-archive-stat">{localizedProjects.length} Projects</span>
            <button type="button" className="project-archive-trigger" onClick={() => setIsArchiveOpen(true)}>
              <span className="project-archive-trigger-plus" aria-hidden="true">
                <img src={`${process.env.PUBLIC_URL}/icons/archive-plus.svg`} alt="" />
              </span>
              {L('전체보기')}
            </button>
          </div>
        </div>

        <div className="project-gallery">
          {visibleProjects.map((project, index) => {
            const rowClassName = index === 0 ? 'project-row project-row-featured' : 'project-row';

            return (
              <article
                key={project.id}
                id={`project-card-${project.id}`}
                className={rowClassName}
                style={{
                  '--project-accent': project.accent,
                  '--project-logo-scale': project.logoScale,
                }}
              >
                <button
                  type="button"
                  className="project-card-trigger"
                  onClick={() => handleOpenProject(project.id)}
                  onMouseEnter={() => syncShowcaseProject(project.id)}
                  onFocus={() => syncShowcaseProject(project.id)}
                >
                  <div className="project-row-logo-stage" aria-hidden="true">
                    <div className="project-row-logo-glow" />
                    <img src={project.logoImage} alt="" className="project-row-logo" />
                  </div>
                  <div className="project-row-content">
                    <div className="project-row-top">
                      <div className="project-row-title-tags">
                        <span className="project-card-number">{project.number}</span>
                        <p className="project-card-category">{project.category}</p>
                      </div>
                      <span className="project-card-year">{project.year}</span>
                    </div>
                    <h3>{project.title}</h3>
                    <p className="project-card-headline">{project.headline}</p>
                    <div className="project-card-meta">
                      <span>{project.role}</span>
                      <span>{project.duration}</span>
                    </div>
                  </div>
                </button>
              </article>
            );
          })}
        </div>
      </animated.div>

      {selectedProject && (typeof document !== 'undefined'
        ? createPortal(
          <div className={detailOverlayClassName} role="dialog" aria-modal="true" aria-labelledby="project-modal-title">
            <button
              type="button"
              className="project-modal-backdrop"
              aria-label="Close project detail"
              onClick={() => setSelectedProjectId(null)}
            />
            <animated.div style={detailSpring} className={detailPanelClassName}>
              <div className="project-modal-head">
                <div className="project-modal-control-row">
                  <div className="project-window-controls" aria-label="Project window controls">
                    <button
                      type="button"
                      className="project-window-control is-red"
                      aria-label="Close project detail"
                      onClick={handleProjectDetailClose}
                    />
                    <button
                      type="button"
                      className="project-window-control is-yellow"
                      aria-label="Preview minimize project detail"
                      onClick={handleDetailMinimize}
                    />
                    <button
                      type="button"
                      className="project-window-control is-green"
                      aria-label={detailWindowMode === 'fullscreen' ? 'Restore project detail' : 'Maximize project detail'}
                      onClick={handleDetailFullscreenToggle}
                    />
                  </div>
                  <span className={getWindowStatusClassName(detailControlNotice, detailWindowFx)}>
                    {renderWindowStatusCode(detailControlNotice)}
                  </span>
                </div>
                <div className="project-modal-info-row">
                  <div className="project-modal-title-group">
                    <p className="project-archive-label">Project</p>
                    <h3 id="project-modal-title" className="project-modal-title">
                      {selectedProject.title}
                    </h3>
                    <p className="project-detail-summary">{selectedProject.summary}</p>
                  </div>
                  <div className="project-detail-side">
                    {detailPeriod && (
                      <p className="project-detail-period">
                        <span>{L('기간')}</span>
                        {detailPeriod}
                      </p>
                    )}
                    <div className="project-detail-meta" aria-label="Project tags">
                      {detailHashtags.map((tag) => (
                        <span key={`${selectedProject.id}-${tag}`} className="project-detail-hashtag">{tag}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="project-modal-content">
                <ProjectDetailSlider key={selectedProject.id} project={selectedProject} localize={L} />
              </div>
            </animated.div>
          </div>,
          document.body,
        )
        : null)}

      {isArchiveOpen && (typeof document !== 'undefined'
        ? createPortal(
          <div className={archiveOverlayClassName} role="dialog" aria-modal="true" aria-labelledby="project-archive-title">
            <button
              type="button"
              className="project-archive-backdrop"
              aria-label="Close project archive"
              onClick={handleArchiveClose}
            />

            <div className={archivePanelClassName}>
              <div className="project-archive-panel-head">
                <div className="project-modal-control-row project-archive-control-row">
                  <div className="project-window-controls" aria-label="Archive window controls">
                    <button
                      type="button"
                      className="project-window-control is-red"
                      aria-label="Close project archive"
                      onClick={handleArchiveClose}
                    />
                    <button
                      type="button"
                      className="project-window-control is-yellow"
                      aria-label="Compact project archive"
                      onClick={handleArchiveMinimize}
                    />
                    <button
                      type="button"
                      className="project-window-control is-green"
                      aria-label={archiveWindowMode === 'fullscreen' ? 'Restore project archive' : 'Maximize project archive'}
                      onClick={handleArchiveFullscreenToggle}
                    />
                  </div>
                  <span className={getWindowStatusClassName(archiveControlNotice, archiveWindowFx)}>
                    {renderWindowStatusCode(archiveControlNotice)}
                  </span>
                </div>
                <div className="project-archive-info-row">
                  <div>
                    <p className="project-archive-label">Archive</p>
                    <h3 id="project-archive-title">{L('프로젝트 아카이브')}</h3>
                  </div>
                  <div className="project-archive-panel-actions">
                    <span className="project-archive-stat">{localizedProjects.length} Projects</span>
                    <span className="project-archive-mode">Selected / Archive</span>
                  </div>
                </div>
              </div>

              <div className="project-archive-content">
                <div className="project-archive-grid">
                  {localizedProjects.map((project) => {
                    const isFeatured = featuredProjects.some((featuredProject) => featuredProject.id === project.id);

                    return (
                      <button
                        type="button"
                        key={`archive-${project.id}`}
                        className={`project-archive-card ${isFeatured ? 'featured' : 'archived'}`}
                        style={{
                          '--project-accent': project.accent,
                          '--archive-logo-scale': project.archiveLogoScale ?? project.logoScale ?? '1',
                          '--archive-logo-shift': project.archiveLogoShift ?? '0px',
                        }}
                        onClick={() => openProjectFromArchive(project.id)}
                      >
                        <div className="project-archive-card-top">
                          <span>{project.number}</span>
                          <span>{project.year}</span>
                        </div>
                        <div className="project-archive-card-body">
                          <div>
                            <h4>{project.title}</h4>
                            <p>{project.headline}</p>
                          </div>
                          <div className="project-archive-card-mark" aria-hidden="true">
                            <img src={project.logoImage} alt="" />
                          </div>
                        </div>
                        <div className="project-archive-card-meta">
                          <span>{project.role}</span>
                          <span>{project.duration}</span>
                          <span>{project.scope}</span>
                        </div>
                        <div className="project-archive-card-foot">
                          {isFeatured ? <strong className="project-archive-featured">Selected</strong> : <strong className="project-archive-featured">Archive</strong>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )
        : null)}
    </section>
  );
};

const ProjectDetailSlider = ({ project, localize }) => {
  const L = localize ?? ((text) => text);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(null);
  const tabRefs = useRef([]);
  const slideRefs = useRef([]);
  const touchStartXRef = useRef(null);
  const touchStartYRef = useRef(null);
  const prototypeImages = (project.prototypeImages ?? [
    project.prototypeImage ? { src: project.prototypeImage, label: `${project.title} Prototype` } : null,
  ]).filter((image) => image?.src);

  const slideItems = [
    {
      title: L('기술스택'),
      content: (
        <div className="project-stack-groups">
          {project.techStack.map((group) => (
            <div key={`${project.id}-${group.label}`} className="project-stack-group">
              <strong>{L(group.label)}</strong>
              <p className="project-stack-inline-text">
                {group.items.map((item) => L(item)).join(' · ')}
              </p>
              <div className="project-chip-list project-chip-list-detailed">
                {group.items.map((item) => (
                  <span key={`${project.id}-${group.label}-${item}`} className="project-chip">
                    {L(item)}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      ),
    },
    {
      title: L('프로젝트 구성도'),
      className: 'project-slide-architecture',
      content: <ProjectArchitectureDiagram project={project} localize={L} />,
    },
    {
      title: L('기여'),
      content: (
        <ul className="project-contribution-list">
          {project.contributions.map((item, index) => (
            <li key={`${project.id}-${item}`} className="project-contribution-item">
              <span className="project-contribution-index">{String(index + 1).padStart(2, '0')}</span>
              <p>{L(item)}</p>
            </li>
          ))}
        </ul>
      ),
    },
    ...(prototypeImages.length > 0 ? [{
      title: L(project.prototypeTabTitle ?? '프로토타입'),
      className: 'project-slide-prototype',
      content: <ProjectPrototypeGallery projectTitle={project.title} images={prototypeImages} localize={L} />,
    }] : []),
    ...(project.introVideo?.src ? [{
      title: L('소개영상'),
      className: 'project-slide-video',
      content: <ProjectIntroVideo projectTitle={project.title} video={project.introVideo} localize={L} />,
    }] : []),
    ...(project.introDocument?.src ? [{
      title: L('소개자료'),
      className: 'project-slide-document',
      content: <ProjectIntroDocument projectTitle={project.title} document={project.introDocument} localize={L} />,
    }] : []),
  ];
  const slides = slideItems.map((slide, index) => ({
    ...slide,
    number: String(index + 1).padStart(2, '0'),
  }));

  const syncViewportHeight = useCallback(() => {
    const activeSlide = slideRefs.current[activeSlideIndex];

    if (!activeSlide) {
      return;
    }

    const nextHeight = Math.ceil(activeSlide.scrollHeight);

    if (!Number.isFinite(nextHeight) || nextHeight <= 0) {
      return;
    }

    setViewportHeight((currentHeight) => {
      if (currentHeight !== null && Math.abs(currentHeight - nextHeight) < 2) {
        return currentHeight;
      }

      return nextHeight;
    });
  }, [activeSlideIndex]);

  useEffect(() => {
    const activeTab = tabRefs.current[activeSlideIndex];

    if (!activeTab || typeof activeTab.scrollIntoView !== 'function') {
      return;
    }

    const useCompactTabScroll = typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(max-width: 920px)').matches;

    activeTab.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: useCompactTabScroll ? 'nearest' : 'center',
    });
  }, [activeSlideIndex]);

  useEffect(() => {
    syncViewportHeight();

    const rafId = window.requestAnimationFrame(syncViewportHeight);
    const timeoutId = window.setTimeout(syncViewportHeight, 180);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
    };
  }, [activeSlideIndex, project.id, syncViewportHeight]);

  useEffect(() => {
    const activeSlide = slideRefs.current[activeSlideIndex];

    if (!activeSlide || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(() => {
      syncViewportHeight();
    });

    resizeObserver.observe(activeSlide);

    return () => {
      resizeObserver.disconnect();
    };
  }, [activeSlideIndex, syncViewportHeight]);

  const handleSliderTouchStart = (event) => {
    const touch = event.touches?.[0];

    if (!touch) {
      return;
    }

    touchStartXRef.current = touch.clientX;
    touchStartYRef.current = touch.clientY;
  };

  const handleSliderTouchEnd = (event) => {
    const touch = event.changedTouches?.[0];

    if (!touch || touchStartXRef.current === null || touchStartYRef.current === null) {
      touchStartXRef.current = null;
      touchStartYRef.current = null;
      return;
    }

    const deltaX = touch.clientX - touchStartXRef.current;
    const deltaY = touch.clientY - touchStartYRef.current;

    touchStartXRef.current = null;
    touchStartYRef.current = null;

    if (Math.abs(deltaX) < 52 || Math.abs(deltaY) > 40) {
      return;
    }

    if (deltaX < 0 && activeSlideIndex < slides.length - 1) {
      setActiveSlideIndex((current) => Math.min(current + 1, slides.length - 1));
      return;
    }

    if (deltaX > 0 && activeSlideIndex > 0) {
      setActiveSlideIndex((current) => Math.max(current - 1, 0));
    }
  };

  return (
    <div className="project-slider">
      <div className="project-slider-tabs" role="tablist" aria-label={`${project.title} detail tabs`}>
        {slides.map((slide, slideIndex) => (
          <button
            key={`${project.id}-${slide.number}-tab`}
            ref={(element) => {
              tabRefs.current[slideIndex] = element;
            }}
            id={`${project.id}-${slide.number}-tab`}
            type="button"
            role="tab"
            aria-selected={slideIndex === activeSlideIndex}
            aria-controls={`${project.id}-panel-${slide.number}`}
            className={`project-slider-tab ${slideIndex === activeSlideIndex ? 'active' : ''}`}
            onClick={() => setActiveSlideIndex(slideIndex)}
          >
            <span className="project-slider-tab-order">{slide.number}</span>
            <span className="project-slider-tab-label">{slide.title}</span>
          </button>
        ))}
      </div>

      <div className="project-slider-viewport" style={viewportHeight ? { height: `${viewportHeight}px` } : undefined}>
        <div
          className="project-slider-track"
          style={{ transform: `translate3d(-${activeSlideIndex * 100}%, 0, 0)` }}
          onTouchStart={handleSliderTouchStart}
          onTouchEnd={handleSliderTouchEnd}
        >
          {slides.map((slide, slideIndex) => (
            <section
              key={`${project.id}-${slide.number}`}
              ref={(element) => {
                slideRefs.current[slideIndex] = element;
              }}
              id={`${project.id}-panel-${slide.number}`}
              role="tabpanel"
              aria-labelledby={`${project.id}-${slide.number}-tab`}
              className={`project-slide ${slide.className ?? ''}`}
            >
              <div className="project-section-head">
                <span>{slide.number}</span>
                <h4>{slide.title}</h4>
              </div>
              {slide.content}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};

const ProjectPrototypeGallery = ({ projectTitle, images, localize }) => {
  const L = localize ?? ((text) => text);

  return (
  <div className={`project-prototype-gallery ${images.length > 1 ? 'multiple' : 'single'}`}>
    {images.map((image) => (
      <figure
        key={`${projectTitle}-${image.src}`}
        className={[
          'project-prototype-card',
          `is-${image.layout ?? 'wide'}`,
          image.column ? `is-column-${image.column}` : '',
        ].filter(Boolean).join(' ')}
      >
        <div className="project-prototype-frame">
          <img src={image.src} alt={`${projectTitle} ${L(image.label)}`} className="project-prototype-image" />
        </div>
        <figcaption className="project-prototype-label">
          <span>{`#${L(image.label).replace(/\s+/g, '')}`}</span>
        </figcaption>
      </figure>
    ))}
  </div>
  );
};

const ProjectIntroVideo = ({ projectTitle, video, localize }) => {
  const L = localize ?? ((text) => text);

  return (
  <div className={`project-video-stage ${video?.src ? 'has-video' : 'is-empty'}`}>
    {video?.src ? (
      <figure className="project-video-card">
        <video
          className="project-video-player"
          src={video.src}
          controls
          preload="metadata"
          playsInline
          aria-label={`${projectTitle} introduction video`}
        />
        <figcaption className="project-video-label">{L(video.label)}</figcaption>
      </figure>
    ) : (
      <div className="project-video-empty" aria-label={`${projectTitle} introduction video is not available`} />
    )}
  </div>
  );
};

const ProjectIntroDocument = ({ projectTitle, document, localize }) => {
  const L = localize ?? ((text) => text);

  if (!document?.src) {
    return null;
  }

  const documentTitle = document.label ? L(document.label) : `${projectTitle} Introduction Document`;
  const viewerSrc = `${document.src}#view=FitH&toolbar=1&navpanes=0`;

  return (
    <div className="project-document-stage">
      <iframe
        className="project-document-frame"
        src={viewerSrc}
        title={`${projectTitle} introduction document`}
        loading="lazy"
      />
      <a
        className="project-document-open"
        href={document.src}
        target="_blank"
        rel="noreferrer"
      >
        {documentTitle}
      </a>
    </div>
  );
};

const ProjectShowcaseDevice = ({ project, media, cameraOrbit }) => {
  const modelViewerRef = useRef(null);
  const textureVersionRef = useRef(0);
  const [isScreenReady, setIsScreenReady] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);

  const applyScreenTexture = useCallback(async () => {
    const modelViewer = modelViewerRef.current;

    if (!modelViewer?.model) {
      return false;
    }

    const screenMaterial = modelViewer.model.materials.find((material) => material.name === SCREEN_MATERIAL_NAME);

    if (!screenMaterial?.emissiveTexture) {
      return false;
    }

    const version = textureVersionRef.current + 1;
    textureVersionRef.current = version;
    setIsScreenReady(false);

    try {
      const textureSource = await buildShowcaseScreenTexture(media, project.accent);

      if (!textureSource || textureVersionRef.current !== version) {
        return false;
      }

      const texture = await modelViewer.createTexture(textureSource);

      if (!texture || textureVersionRef.current !== version) {
        return false;
      }

      screenMaterial.emissiveTexture.setTexture(texture);
      screenMaterial.setEmissiveFactor([1, 1, 1]);
      setIsScreenReady(true);
      return true;
    } catch (error) {
      console.error('Failed to apply showcase texture', error);
      setIsScreenReady(true);
      return false;
    }
  }, [media, project.accent]);

  useEffect(() => {
    setIsScreenReady(false);
    setLoadingStep(0);
  }, [media?.src, media?.type, project.id]);

  useEffect(() => {
    if (isScreenReady) {
      return undefined;
    }

    const loadingTimer = window.setInterval(() => {
      setLoadingStep((currentStep) => (currentStep + 1) % 4);
    }, 360);

    return () => {
      window.clearInterval(loadingTimer);
    };
  }, [isScreenReady]);

  useEffect(() => {
    const modelViewer = modelViewerRef.current;

    if (!modelViewer) {
      return undefined;
    }

    const handleLoad = () => {
      applyScreenTexture();
    };

    modelViewer.addEventListener('load', handleLoad);
    handleLoad();

    return () => {
      modelViewer.removeEventListener('load', handleLoad);
    };
  }, [applyScreenTexture]);

  useEffect(() => {
    applyScreenTexture();
  }, [applyScreenTexture]);

  const deviceFloatClassName = [
    'projects-device-float',
    isScreenReady ? 'is-ready' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className="projects-device-model">
      {!isScreenReady && (
        <div className="projects-device-loading" aria-live="polite">
          <span>loading</span>
          <span className="projects-device-loading-dots">{'.'.repeat(loadingStep)}</span>
        </div>
      )}
      <div className={deviceFloatClassName}>
        <model-viewer
          ref={modelViewerRef}
          className="projects-model-viewer"
          src={PHONE_MODEL_SRC}
          alt="3D smartphone model"
          exposure="1.05"
          shadow-intensity="1.2"
          shadow-softness="0.8"
          camera-orbit={cameraOrbit}
          field-of-view="20deg"
          min-field-of-view="20deg"
          max-field-of-view="20deg"
          interaction-prompt="none"
          disable-zoom
          disable-pan
          touch-action="none"
        />
      </div>
    </div>
  );
};

const ProjectArchitectureDiagram = ({ project, localize }) => {
  const L = localize ?? ((text) => text);
  const { columns, bridges } = project.systemDiagram;

  if (project.systemDiagram.image) {
    return (
      <figure className={`project-architecture-image-stage is-${project.systemDiagram.imageTone ?? 'dark'}`}>
        <img
          src={project.systemDiagram.image}
          alt={project.systemDiagram.imageAlt ? L(project.systemDiagram.imageAlt) : `${project.title} architecture diagram`}
          className="project-architecture-image"
        />
      </figure>
    );
  }

  return (
    <div className="project-system-diagram" role="img" aria-label={`${project.title} architecture diagram`}>
      {columns.map((column, index) => (
        <React.Fragment key={`${project.id}-${column.title}`}>
          <div className="project-system-column">
            <div className="project-system-column-head">
              <strong className="project-system-layer-title">
                <span className="project-system-layer-index">{String(index + 1).padStart(2, '0')}</span>
                <span className="project-system-layer-name">{L(column.title)}</span>
              </strong>
            </div>
            <div className="project-system-blocks">
              {column.blocks.map((block) => (
                <div key={`${project.id}-${column.title}-${block.title}`} className="project-system-block">
                  <h5>{L(block.title)}</h5>
                  <div className="project-system-items">
                    {block.items.map((item) => (
                      <span key={`${project.id}-${block.title}-${item}`} className="project-system-chip">
                        {L(item)}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {index < bridges.length && (
            <div className="project-system-bridge" aria-hidden="true">
              <span className="project-system-bridge-protocol">{L(bridges[index])}</span>
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default Projects;
